'use client'

import { parseAsStringEnum, useQueryState } from 'nuqs'
import { useEffect, useRef } from 'react'
import { SERVICE_PURCHASE_STEP_VALUES } from '@/modules/service-purchase/lib/constants'
import { useServicePurchaseStep } from '../../providers/service-purchase-store-provider'

function StepUrlSync() {
  const { activeStep, goToStep } = useServicePurchaseStep()
  // No default: keeps `?step=` absent when omitted so nuqs does not clobber the URL; step comes from persist/store.
  const [stepFromUrl, setStepFromUrl] = useQueryState(
    'step',
    parseAsStringEnum(SERVICE_PURCHASE_STEP_VALUES),
  )
  /** Last `step` query value we pushed into the store (`undefined` = not yet read for this mount). */
  const prevSyncedUrlStepRef = useRef<typeof stepFromUrl | undefined>(undefined)
  const isSyncingFromUrlRef = useRef<boolean>(false)

  // URL -> Store: explicit `?step=` overrides store (including on first paint when URL already has step)
  useEffect(() => {
    if (stepFromUrl === null) {
      prevSyncedUrlStepRef.current = null
      return
    }
    if (prevSyncedUrlStepRef.current !== stepFromUrl) {
      prevSyncedUrlStepRef.current = stepFromUrl
      isSyncingFromUrlRef.current = true
      goToStep(stepFromUrl)
      queueMicrotask(() => {
        isSyncingFromUrlRef.current = false
      })
    }
  }, [stepFromUrl, goToStep])

  // Store -> URL: when store changed (user nav, hydration), sync to URL
  useEffect(() => {
    if (isSyncingFromUrlRef.current || stepFromUrl === activeStep) return
    // On first write when there is no ?step= param yet, use 'replace'
    // to avoid creating an extra history entry.
    // Use 'push' on step changes so that the browser back button
    // walks the wizard steps correctly.
    void setStepFromUrl(activeStep, {
      history: stepFromUrl === null ? 'replace' : 'push',
    })
  }, [activeStep, stepFromUrl, setStepFromUrl])

  return null
}

export { StepUrlSync }
